import { Button } from "../components/Button";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const ButtonsSwitchContext = React.createContext(null);
const useButtonsSwitch = () => {
    const ctx = React.useContext(ButtonsSwitchContext);
    if (!ctx) {
        throw new Error("ButtonsSwitch must be used within a ButtonsSwitchList component");
    }
    return ctx;
};
const listStyles = cva(cn("relative isolate inline-flex items-center gap-1", "box-border bg-background border border-border-dark"), {
    variants: {
        fullWidth: {
            true: "w-full",
            false: "",
        },
        size: {
            xs: "rounded-xl p-[3px]",
            sm: "rounded-2xl p-1",
            md: "rounded-3xl p-1.5",
        },
    },
    defaultVariants: {
        fullWidth: false,
        size: "sm",
    },
});
export const ButtonsSwitchList = React.forwardRef(({ className, children, size = "sm", value, defaultValue, onValueChange, disabled, fullWidth, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const isControlled = value !== undefined;
    const selected = isControlled ? value : internalValue;
    const handleChange = React.useCallback((next) => {
        if (!isControlled) {
            setInternalValue(next);
        }
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(next);
    }, [isControlled, onValueChange]);
    const context = React.useMemo(() => ({ value: selected, onValueChange: handleChange, size, disabled }), [selected, handleChange, size, disabled]);
    const listRef = React.useRef(null);
    const setRefs = React.useCallback((node) => {
        listRef.current = node;
        if (typeof ref === "function") {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
    }, [ref]);
    const indicator = useSelectedIndicator(listRef, selected);
    return (React.createElement("div", { ref: setRefs, role: "tablist", "aria-orientation": "horizontal", className: cn(listStyles({ fullWidth, size }), className), ...props },
        React.createElement("span", { "aria-hidden": true, className: cn("pointer-events-none absolute left-0 top-0 -z-10 border", "border-highlight-200 bg-highlight-50 shadow-xs", "dark:border-highlight-500/40 dark:bg-highlight-500/15", indicatorRadius[size], indicator.animate &&
                "transition-[transform,width,height,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none", indicator.box === null ? "opacity-0" : "opacity-100"), style: indicator.box === null
                ? undefined
                : {
                    width: indicator.box.width,
                    height: indicator.box.height,
                    transform: `translate(${indicator.box.left}px, ${indicator.box.top}px)`,
                } }),
        React.createElement(ButtonsSwitchContext.Provider, { value: context }, children)));
});
ButtonsSwitchList.displayName = "ButtonsSwitchList";
const indicatorRadius = {
    xs: "rounded-lg",
    sm: "rounded-xl",
    md: "rounded-2xl",
};
// Where the selected tab sits inside the list, re-measured when the list
// resizes (a sidebar collapsing, a label wrapping), so the pill tracks it.
function useSelectedIndicator(listRef, selected) {
    const [box, setBox] = React.useState(null);
    const [animate, setAnimate] = React.useState(false);
    React.useLayoutEffect(() => {
        const list = listRef.current;
        if (!list) {
            return;
        }
        const measure = () => {
            const tab = selected === undefined
                ? null
                : list.querySelector(`[role="tab"][data-value="${CSS.escape(selected)}"]`);
            setBox(tab
                ? {
                    left: tab.offsetLeft,
                    top: tab.offsetTop,
                    width: tab.offsetWidth,
                    height: tab.offsetHeight,
                }
                : null);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(list);
        return () => observer.disconnect();
    }, [listRef, selected]);
    React.useEffect(() => {
        if (box !== null && !animate) {
            const frame = requestAnimationFrame(() => setAnimate(true));
            return () => cancelAnimationFrame(frame);
        }
    }, [box, animate]);
    return { box, animate };
}
export const ButtonsSwitch = React.forwardRef(({ className, value, label, icon, disabled, onClick, ...props }, ref) => {
    const { value: selected, onValueChange, size, disabled: groupDisabled, } = useButtonsSwitch();
    const isActive = selected === value;
    const isDisabled = disabled || groupDisabled;
    const handleClick = (e) => {
        if (isDisabled) {
            return;
        }
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(value);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    return (React.createElement(Button, { ref: ref, role: "tab", "aria-selected": isActive, "data-value": value, size: size, variant: "ghost", label: label, icon: icon, className: cn(
        // The list's sliding pill paints the selected background; the tab
        // only shifts its label colour, and dips slightly when pressed.
        "border border-transparent transition-[color,transform] duration-200 ease-out active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100", isActive &&
            "bg-transparent text-highlight-700 hover:bg-transparent dark:bg-transparent dark:text-highlight-300 dark:hover:bg-transparent", className), disabled: isDisabled, onClick: handleClick, ...props }));
});
ButtonsSwitch.displayName = "ButtonsSwitch";
//# sourceMappingURL=ButtonsSwitch.js.map